import { buildQueryParams, requestBuilder } from '../../http/RequestBuilder';
import type {
    DbosCronsResponse,
    DbosDurationStatsParams,
    DbosDurationStatsResponse,
    DbosForkResponse,
    DbosQueuesResponse,
    DbosQueueTimelineParams,
    DbosQueueTimelineResponse,
    DbosSummaryResponse,
    DbosTimelineParams,
    DbosTimelineResponse,
    DbosTimeRangeParams,
    DbosWorkflowDetailResponse,
    DbosWorkflowListItem,
    DbosWorkflowListParams,
} from '../types/DbosTypes';

const BASE_PATH = '/api/admin/dbos-dashboard';

// --- Helper to build query string from optional params ---

const buildOptionalQueryString = (
    params: Record<string, string | number | boolean | undefined>,
): string => {
    const filtered: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
            filtered[key] = value;
        }
    }
    if (Object.keys(filtered).length === 0) return '';
    return buildQueryParams(filtered);
};

// --- 1. GET /summary ---

export const useGetDbosSummary = requestBuilder.query<
    DbosSummaryResponse,
    DbosTimeRangeParams
>({
    endpoint: (params) =>
        `${BASE_PATH}/summary${buildOptionalQueryString({
            startTime: params.startTime,
            endTime: params.endTime,
        })}`,
    queryKey: (params) =>
        `dbos-summary/${params.startTime ?? 'default'}/${params.endTime ?? 'default'}`,
});

// --- 2. GET /timeline ---

export const useGetDbosTimeline = requestBuilder.query<
    DbosTimelineResponse,
    DbosTimelineParams
>({
    endpoint: (params) =>
        `${BASE_PATH}/timeline${buildOptionalQueryString({
            startTime: params.startTime,
            endTime: params.endTime,
            interval: params.interval,
            workflowName: params.workflowName,
            configName: params.configName,
        })}`,
    queryKey: (params) =>
        `dbos-timeline/${params.startTime ?? ''}/${params.endTime ?? ''}/${params.interval ?? ''}/${params.workflowName ?? ''}/${params.configName ?? ''}`,
});

// --- 3. GET /workflows (list) ---

export const useGetDbosWorkflows = requestBuilder.query<
    DbosWorkflowListItem[],
    DbosWorkflowListParams
>({
    endpoint: (params) =>
        `${BASE_PATH}/workflows${buildOptionalQueryString({
            status: params.status,
            workflowName: params.workflowName,
            startTime: params.startTime,
            endTime: params.endTime,
            limit: params.limit,
            offset: params.offset,
            sortDesc: params.sortDesc,
        })}`,
    queryKey: (params) =>
        `dbos-workflows/${params.status ?? ''}/${params.workflowName ?? ''}/${params.startTime ?? ''}/${params.endTime ?? ''}/${params.limit ?? 50}/${params.offset ?? 0}/${params.sortDesc ?? true}`,
});

// --- 4. GET /workflows/:workflowId (detail) ---

export const useGetDbosWorkflowDetail = requestBuilder.query<
    DbosWorkflowDetailResponse,
    { workflowId: string }
>({
    endpoint: ({ workflowId }) => `${BASE_PATH}/workflows/${workflowId}`,
    queryKey: ({ workflowId }) => `dbos-workflow-detail/${workflowId}`,
});

// --- 5. GET /queues ---

export const useGetDbosQueues = requestBuilder.query<
    DbosQueuesResponse,
    Record<string, never>
>({
    endpoint: () => `${BASE_PATH}/queues`,
    queryKey: () => 'dbos-queues',
});

// --- 6. GET /queues/timeline ---

export const useGetDbosQueueTimeline = requestBuilder.query<
    DbosQueueTimelineResponse,
    DbosQueueTimelineParams
>({
    endpoint: (params) =>
        `${BASE_PATH}/queues/timeline${buildOptionalQueryString({
            startTime: params.startTime,
            endTime: params.endTime,
            interval: params.interval,
            queueName: params.queueName,
        })}`,
    queryKey: (params) =>
        `dbos-queue-timeline/${params.startTime ?? ''}/${params.endTime ?? ''}/${params.interval ?? ''}/${params.queueName ?? ''}`,
});

// --- 7. GET /duration-stats ---

export const useGetDbosDurationStats = requestBuilder.query<
    DbosDurationStatsResponse,
    DbosDurationStatsParams
>({
    endpoint: (params) =>
        `${BASE_PATH}/duration-stats${buildOptionalQueryString({
            startTime: params.startTime,
            endTime: params.endTime,
            workflowName: params.workflowName,
        })}`,
    queryKey: (params) =>
        `dbos-duration-stats/${params.startTime ?? ''}/${params.endTime ?? ''}/${params.workflowName ?? ''}`,
});

// --- 8. GET /scheduled-crons ---

export const useGetDbosScheduledCrons = requestBuilder.query<
    DbosCronsResponse,
    Record<string, never>
>({
    endpoint: () => `${BASE_PATH}/scheduled-crons`,
    queryKey: () => 'dbos-scheduled-crons',
});

// --- 9. POST /workflows/:workflowId/fork ---

export const useForkDbosWorkflow = requestBuilder.mutation<
    DbosForkResponse,
    { startStep: number },
    { workflowId: string }
>({
    endpoint: ({ workflowId }) => `${BASE_PATH}/workflows/${workflowId}/fork`,
    method: 'POST',
});
